import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton,
  CircularProgress, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Chip
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageIcon from '@mui/icons-material/Storage';
import { getBackups, createBackup, deleteBackup, restoreBackup } from '../api';

function formatBytes(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export default function BackupManager({ serverName, status }) {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchBackups = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getBackups(serverName);
      setBackups(data);
    } catch (e) {
      console.error("Failed to fetch backups", e);
    } finally {
      setLoading(false);
    }
  }, [serverName]);

  useEffect(() => {
    fetchBackups();
  }, [fetchBackups]);

  const handleCreateBackup = async () => {
    setActionLoading(true);
    try {
      await createBackup(serverName);
      fetchBackups();
    } catch (e) {
      alert(`Backup failed: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteBackup = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      await deleteBackup(serverName, deleteTarget);
      setDeleteTarget(null);
      fetchBackups();
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestoreBackup = async () => {
    if (!restoreTarget) return;
    setActionLoading(true);
    try {
      await restoreBackup(serverName, restoreTarget);
      setRestoreTarget(null);
      alert("Backup restored successfully!");
    } catch (e) {
      alert(`Restore failed: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Card sx={{
      background: 'rgba(30, 30, 40, 0.4)',
      backdropFilter: 'blur(16px)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      boxShadow: '0 8px 32px 0 rgba(0,0,0,0.3)',
      borderRadius: 4,
      overflow: 'hidden'
    }}>
      <Box sx={{
        p: 2.5,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        background: 'linear-gradient(90deg, rgba(0,230,118,0.05) 0%, rgba(0,0,0,0) 100%)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <StorageIcon sx={{ color: '#00E676', filter: 'drop-shadow(0 0 8px rgba(0,230,118,0.4))' }} />
          <Typography variant="h6" sx={{ fontWeight: '800', fontFamily: 'Outfit, sans-serif', letterSpacing: '0.5px' }}>
            System Backups
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" onClick={fetchBackups} disabled={loading || actionLoading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={handleCreateBackup}
            disabled={actionLoading || loading}
            startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
            sx={{
              fontWeight: '700',
              borderRadius: 2,
              textTransform: 'none',
              px: 2,
              background: 'linear-gradient(135deg, #00E676 0%, #00B0FF 100%)',
              boxShadow: '0 0 12px rgba(0,230,118,0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #00C853 0%, #0091EA 100%)',
                boxShadow: '0 0 20px rgba(0,230,118,0.5)',
              }
            }}
          >
            {actionLoading ? 'Creating...' : 'Create Backup'}
          </Button>
        </Box>
      </Box>

      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        {loading && backups.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress color="success" />
          </Box>
        ) : backups.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center', opacity: 0.6 }}>
            <Typography variant="body1" gutterBottom sx={{ fontFamily: 'Inter, sans-serif' }}>
              No backups created yet.
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              Create a backup to protect your server worlds from data loss!
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: 'transparent', fontWeight: 'bold', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>Backup Archive</TableCell>
                  <TableCell sx={{ bgcolor: 'transparent', fontWeight: 'bold', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>File Size</TableCell>
                  <TableCell sx={{ bgcolor: 'transparent', fontWeight: 'bold', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>Created At</TableCell>
                  <TableCell align="right" sx={{ bgcolor: 'transparent', fontWeight: 'bold', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {backups.map((backup) => (
                  <TableRow key={backup.name} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)', py: 1.5, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {backup.name}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <Chip label={formatBytes(backup.size)} size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.15)', fontSize: '0.75rem' }} />
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: '0.8rem', color: 'text.secondary' }}>
                      {new Date(backup.mtime).toLocaleString()}
                    </TableCell>
                    <TableCell align="right" sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Tooltip title="Restore world from backup">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => setRestoreTarget(backup.name)}
                            disabled={actionLoading}
                            sx={{ '&:hover': { bgcolor: 'rgba(0,176,255,0.1)' } }}
                          >
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete backup">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteTarget(backup.name)}
                            disabled={actionLoading}
                            sx={{ '&:hover': { bgcolor: 'rgba(211,47,47,0.1)' } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {/* Restore Dialog */}
      <Dialog open={!!restoreTarget} onClose={() => !actionLoading && setRestoreTarget(null)}>
        <DialogTitle sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 'bold' }}>Restore Backup?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.secondary', mb: 1.5 }}>
            Are you sure you want to restore the backup <strong>{restoreTarget}</strong>?
          </DialogContentText>
          <Typography variant="body2" color="warning.main" sx={{ p: 1.5, bgcolor: 'rgba(255,167,38,0.08)', borderRadius: 2, border: '1px solid rgba(255,167,38,0.2)' }}>
            ⚠️ <strong>Warning:</strong> This will stop the server, delete your active world directory entirely, and roll it back. This action is irreversible!
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
          <Button onClick={() => setRestoreTarget(null)} disabled={actionLoading} variant="outlined" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleRestoreBackup} disabled={actionLoading} variant="contained" color="warning" sx={{ textTransform: 'none' }}>
            {actionLoading ? 'Restoring...' : 'Restore Backup'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => !actionLoading && setDeleteTarget(null)}>
        <DialogTitle sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 'bold' }}>Delete Backup?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.secondary' }}>
            Are you sure you want to delete the backup file <strong>{deleteTarget}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={actionLoading} variant="outlined" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteBackup} disabled={actionLoading} variant="contained" color="error" sx={{ textTransform: 'none' }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
